import { Box, Text, Button } from '@csscompany/csds-components';
import {
  FacilityPage,
  initFacilityData,
  parseFacilityData,
  rosetta,
  useFacilitiesInfo,
} from '@csscompany/op-facility-finder';
import { isBrowser } from '@publicss/utils';
import { graphql } from 'gatsby';
import React, { useCallback, useEffect, useState } from 'react';
import 'react-phone-input-2/lib/style.css';
import styled from 'styled-components';

import { Meta } from '../components/Meta';
import { FacilityCard } from '../components/kitchen-growth/FacilityCard';
import { LoadingState } from '../components/kitchen-growth/LoadingState';
// TODO: remove this on the next CSDS version
import { Button as CKButton } from '../components/ui';
import { LocationsSearchBox } from '../components/ui/LocationsSearchBox';
import {
  BM_SCROLL_CONTAINER_ID,
  LOG_PRODUCT_NAME,
  FACILITY_FINDER_COMPONENT,
  LOCATIONS_COMPONENT_NAME,
  BOOK_TOUR_COMPONENT,
} from '../constants/facilityConstants';
import { CONFIG } from '../constants/mapConfig';
import { getBreadcrumbSchema } from '../seo/getBreadcrumbSchema';
import { useIsDesktop } from '../utils/breakpoints';
import { getFormattedFacilityData } from '../utils/getFormattedFacilityData';
import { useMixpanel } from '../utils/mixpanel';

const MaterialMap = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="1em"
    height="1em">
    {' '}
    <path fill="none" d="M0 0h24v24H0z" />
    <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z" />
  </svg>
);

const MaterialList = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="1em"
    height="1em">
    <path fill="none" d="M0 0h24v24H0z" />
    <path d="M19 5v14H5V5h14m1.1-2H3.9c-.5 0-.9.4-.9.9v16.2c0 .4.4.9.9.9h16.2c.4 0 .9-.5.9-.9V3.9c0-.5-.5-.9-.9-.9zM11 7h6v2h-6V7zm0 4h6v2h-6v-2zm0 4h6v2h-6zM7 7h2v2H7zm0 4h2v2H7zm0 4h2v2H7z" />
  </svg>
);

const Section = styled.section`
  background: ${(props) => props.theme.colors.$staticBackgroundStandard};
  overflow: hidden;
  position: fixed;

  @media (min-width: 800px) {
    .close-button {
      div {
        padding: 7px 15px !important;
      }
    }
  }

  #more_nearby_divider {
    text-align: start;
  }

  #map_container {
    display: none;
  }

  .mapboxgl-map {
    position: fixed;
  }

  @media (min-width: 1120px) {
    .close-button {
      div {
        padding: 7px 11px !important;
      }
    }

    #map_container {
      display: block;
      position: fixed;
      inset-inline-start: 132px;
      inset-inline-end: 0;
    }
    .mapboxgl-map {
      position: fixed;
      display: relative;
    }
  }

  @media (min-width: 1120px) {
    display: flex;
    width: 100%;
    overflow: scroll !important;
    height: 100vh;
    position: flex;
    overflow: hidden !important;
  }

  [data-testid='legend-container'] {
    top: 163px;
    @media (min-width: 1120px) {
      inset-inline-end: 140px;
    }
  }
  #map-interaction-controls {
    position: fixed;
  }

  .preview-typeform {
    margin-top: 11px;
  }

  [data-testid='map-tool-box'] {
    @media (min-width: 1120px) {
      inset-inline-end: 140px;
    }
  }
`;

export const Head = ({ data, pageContext, location: { pathname } }) => {
  const { siteName, siteURL, slug } = pageContext;
  const { seo_title, seo_description, no_index } =
    data?.prismicPage?.data || {};

  return (
    <>
      <Meta
        pageTitle={
          seo_title || 'Commercial Kitchens for Restaurant Delivery + Pickup'
        }
        title={
          seo_title || 'Commercial Kitchens for Restaurant Delivery + Pickup'
        }
        description={
          seo_description ||
          `Explore ${siteName} locations. Each commercial kitchen location is built for food delivery and scale, to help you reach more customers.`
        }
        url={`${siteURL}${slug}`}
        pageContext={pageContext}
        noIndex={no_index}
      />
      <script type="application/ld+json">
        {getBreadcrumbSchema(pathname)}
      </script>
    </>
  );
};

export default function Locations({ pageContext }) {
  const {
    setData,
    setFacilitiesData,
    setMapClickedFacilityId,
    setIsShowMap,
    isShowMap,
    setFacilityScrollTop,
    setIsGoDetail,
    facilitiesData,
  } = useFacilitiesInfo();

  const mixpanelClient = useMixpanel();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    const mapContainer = document.getElementById('map_container');

    if (isDesktop && mapContainer) {
      setIsShowMap(false);
      document.getElementById('map_container').style['display'] = 'block';
      document.getElementById('map_container').style['position'] = 'fixed';
    } else if (isShowMap === false && mapContainer) {
      document.getElementById('map_container').style['display'] = 'none';
    }
  }, [isDesktop]);

  useEffect(() => {
    if (facilitiesData.length === 0) {
      initFacilityData([pageContext.siteTag], pageContext.siteLang).then(
        (data) => {
          const { cleanData, facilities } = parseFacilityData(data);

          const facilityData = facilities.map((facility) =>
            getFormattedFacilityData(facility)
          );

          setData(cleanData);
          setFacilitiesData(facilityData);
        }
      );
    }
    return () => {
      setMapClickedFacilityId(undefined);
      setIsShowMap(false);
      setFacilityScrollTop(undefined);
      setIsGoDetail(false);
    };
  }, [pageContext.siteLang, pageContext.siteTag]);

  const scrollUp = () => {
    if (isBrowser()) {
      window?.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    const mapContainer = document.getElementById('map_container');
    const tl = document.getElementsByClassName('tl-edges')[0];

    if (mapContainer && tl) {
      if (!isShowMap && !isDesktop) {
        document.getElementById('map_container').style['display'] = 'none';
        document.getElementsByClassName('tl-edges')[0].style['height'] = '100%';
        document.getElementsByClassName('tl-edges')[0].style['width'] = '100%';

        scrollUp();
      } else if (!isDesktop && isShowMap) {
        document.getElementById('map_container').style['display'] = 'block';
        document.getElementById('map_container').style['position'] = 'fixed';

        document.getElementsByClassName('tl-edges')[0].style['height'] =
          '100vh';
        document.getElementsByClassName('tl-edges')[0].style['width'] = '100vw';
        scrollUp();
      } else {
        document.getElementsByClassName('tl-edges')[0].style['height'] = '100%';
        document.getElementsByClassName('tl-edges')[0].style['width'] = '100%';
      }
    }
  }, [isShowMap]);

  function mixpanelOpen() {
    const mixpanelClient = useMixpanel();

    mixpanelClient.track(
      {
        product: LOG_PRODUCT_NAME,
        component: BOOK_TOUR_COMPONENT,
        event: 'click',
      },
      {
        position: 'Locations page - preview card',
      }
    );
  }

  const onShowMap = useCallback(() => setIsShowMap(true), []);
  const onShowList = useCallback(() => setIsShowMap(false), []);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    mixpanelClient.track({
      product: LOG_PRODUCT_NAME,
      component: FACILITY_FINDER_COMPONENT,
      event: 'Locations Page | Landed',
    });

    setTimeout(() => setLoading(false), 1000);
  }, []);

  function mixpanelViewDetail(facility) {
    if (facility) {
      mixpanelClient.track({
        product: LOG_PRODUCT_NAME,
        component: `${LOCATIONS_COMPONENT_NAME} | Preview card`,
        event: 'click view details',
      });
    }
  }

  function mixpanelMapClicked(mapClickedFacilityId) {
    if (mapClickedFacilityId) {
      mixpanelClient.track({
        product: LOG_PRODUCT_NAME,
        component: LOCATIONS_COMPONENT_NAME,
        event: 'select facility',
      });
    }
  }

  return (
    <div className="min-h-screen">
      {loading ? (
        <LoadingState />
      ) : (
        <Section
          style={{
            overflow: 'hidden',
            paddingBottom: isDesktop ? '69px' : '45px',
            position: isDesktop ? 'fixed' : isShowMap ? 'fixed' : 'relative',
          }}>
          <Box
            dataTest={CONFIG.LOCATIONS_TEST_ID}
            display={isDesktop ? 'flex' : 'fixed'}
            flexDirection="column"
            alignItems="center"
            flexShrink="0"
            flexGrow="1">
            <Box
              style={
                isDesktop
                  ? { padding: '26px 18px' }
                  : {
                      padding: '16px',
                      position: 'fixed',
                      insetBlockStart: '68px',
                      insetInlineStart: '0px',
                    }
              }
              justifyContent="space-between"
              borderBottom="1px solid var(--grey-3)"
              background="var(--grey-2)"
              display="flex"
              zIndex="10"
              flexGrow="1"
              width="100%"
              gap="$2"
              dataTest="filter-box">
              {isDesktop && (
                <Box display="flex" width="fit-content">
                  <Text variant="$headingLarge">
                    {pageContext.siteTag === 'site:cocinas-ocultas' ? (
                      <h1>Cocinas Ocultas cerca de mi</h1>
                    ) : (
                      <h1>{rosetta.locations_page_title.getText()}</h1>
                    )}
                  </Text>
                </Box>
              )}
              <Box minWidth={240}>
                <LocationsSearchBox siteLang={pageContext.siteLang} />
              </Box>

              {!isDesktop && (
                <Box
                  className="min-w-[80px] rounded-[8px]"
                  backgroundColor="$staticDividerWeak"
                  display="flex">
                  <Button
                    className="w-[40px]"
                    emphasis="low"
                    icon={MaterialList}
                    variant="secondary"
                    iconPlacement="trailing"
                    selected={!isShowMap}
                    onClick={onShowList}
                  />
                  <Button
                    className="w-[40px]"
                    dataTest="show-map-button"
                    emphasis="low"
                    icon={MaterialMap}
                    variant="secondary"
                    iconPlacement="trailing"
                    selected={isShowMap}
                    onClick={onShowMap}
                  />
                </Box>
              )}
            </Box>
            <Box
              overflow="scroll"
              width="100%"
              height="100%"
              paddingTop={!isDesktop ? '85px' : '0px'}>
              <FacilityPage
                TypeformButton={
                  <CKButton globalTypeform utm_content="ff-map-cta" size="sm">
                    <Text variant="$labelSmallBold">
                      {rosetta.facility_card_book_tour_button.getText()}
                    </Text>
                  </CKButton>
                }
                BM_SCROLL_CONTAINER_ID={BM_SCROLL_CONTAINER_ID}
                FacilityCard={FacilityCard}
                mixpanel={{
                  open: mixpanelOpen,
                  viewDetail: mixpanelViewDetail,
                  mapClicked: mixpanelMapClicked,
                }}
              />
            </Box>
          </Box>
        </Section>
      )}
    </div>
  );
}

export const query = graphql`
  query ($siteTag: String!, $siteLang: String!) {
    prismicPage(
      data: { slug: { eq: "/locations/" }, locale: { eq: $siteLang } }
      tags: { eq: $siteTag }
    ) {
      _previewable
      data {
        slug
        locale
        seo_title
        seo_description
        no_index
      }
    }
  }
`;
